import { Delete } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  FormHelperText,
  IconButton,
  Input,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import * as Yup from 'yup';

import { showError } from '../../constants/toasts';
import { errorToast, successToast } from '../../helpers';
import MainContainer from '../../layout/MainContainer';
import {
  useAddBannerMutation,
  useLazyGetBannerByIdQuery,
  useUpdateBannerMutation,
} from '../../service/banner';
import { useLazyGetMasterDataQuery } from '../../service/common';
import { BannerData, CommonBody, MasterData } from '../../types/General';
import { generateEncryptedKeyBody } from '../../utils/crypto';
import { BANNER_TYPE, USER_TYPE } from '../../utils/enums';
import { UploadMedia, UploadVideo } from '../../utils/mediaUpload';

const AddPromotionalCampaign = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [masterData, setMasterData] = useState<MasterData>();
  const [data, setData] = useState<BannerData>();
  const [linkType, setLinkType] = useState<'Video' | 'Link'>('Video');
  const [linkData, setLinkData] = useState<string>('');
  const [promotionalLinks, setPromotionalLinks] = useState<any>([]);
  const [addBanner] = useAddBannerMutation();
  const [getMasterData] = useLazyGetMasterDataQuery();
  const [getBannerById] = useLazyGetBannerByIdQuery();
  const [updateBanner] = useUpdateBannerMutation();

  const formSchema = Yup.object({
    image: Yup.string().required('Image is required'),
    title: Yup.string().required('Title is required'),
    sellerId: Yup.string().required('This field is required'),
    // link: Yup.string().required("Link is required"),
    userType: Yup.string().required('User Type is required'),
  });
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      image: data?.image || '',
      title: data?.name || '',
      sellerId: data?.user?._id || '',
      link: data?.link || '',
      userType: data?.user?.role || '',
      bannerType: data?.bannerType || '',
    },
    validationSchema: formSchema,
    onSubmit: async (values: any) => {
      if (!promotionalLinks?.length) {
        showError('Promotional link/video is required');
        return;
      }
      try {
        const body = {
          // link: values.link,
          image: values.image,
          name: values.title,
          userId: values.sellerId,
          bannerType: values.bannerType,
          link: promotionalLinks,
        };
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        let response;
        if (id) {
          response = await updateBanner({ body: encryptedBody, id }).unwrap();
        } else {
          response = await addBanner({ body: encryptedBody }).unwrap();
        }
        if (response?.statusCode === 200) {
          successToast(id ? 'Campaign updated successfully' : 'Campaign Added Successfully');
          navigate('/promotional-campaigns');
        }
      } catch (error: any) {
        errorToast(error?.data?.message);
      }
    },
  });

  const handlePromotional = () => {
    if (!linkData) {
      return;
    }
    if (typeof promotionalLinks == 'undefined') {
      setPromotionalLinks({ linkType: linkType === 'Video' ? 1 : 2, link: linkData });
    } else {
      setPromotionalLinks([
        ...promotionalLinks,
        { link: linkData, linkType: linkType === 'Video' ? 1 : 2 },
      ]);
    }
    setLinkData('');
  };

  const handleLinkVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input?.files?.[0];

    if (!file) return;

    //  Type check
    if (file.type !== 'video/mp4') {
      errorToast('Invalid video type. Only MP4 is allowed.');
      return;
    }

    try {
      const images = await UploadVideo(file);
      if (images?.statusCode === 200) {
        setLinkData(images?.data?.image);
        input.value = '';
      } else {
        errorToast('Upload failed. Please try again.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      errorToast('Something went wrong during upload.');
    }
  };

  const handleCoverUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target;
    const file = files?.files?.length ? files?.files[0] : '';
    // if (Array.from(files || []).length > 20) {
    //   errorToast("Max limit is upto 20 images");
    //   event.target.value = "";
    //   return;
    // }
    if (
      file &&
      file.type &&
      file.type !== 'image/jpeg' &&
      file.type !== 'image/jpg' &&
      file.type !== 'image/png'
    ) {
      errorToast('Invalid image type');
      return;
    }

    const images = await UploadMedia(file);
    if (images?.statusCode === 200) {
      // setCoverImage(images?.data?.image);
      formik.setFieldValue('image', images?.data?.image);
      event.target.value = '';
    }
  };

  const handleSellerChange = (event: SelectChangeEvent) => {
    formik.setFieldValue('sellerId', event.target.value);
  };

  const fetchMasterData = async (userType: string) => {
    // setLoading(true);
    try {
      const res = await getMasterData({
        search: 'userType',
        ...(userType ? { role: Number(formik.values?.userType) } : {}),
      }).unwrap();
      if (res?.statusCode === 200) {
        setMasterData(res?.data);
        // setLoading(false);
      }
    } catch (error: any) {
      console.error(error);
      // setLoading(false);
    }
  };

  const fetchBanner = async (id: string) => {
    try {
      const res = await getBannerById({ id }).unwrap();
      if (res?.statusCode === 200) {
        setData(res?.data);
        setPromotionalLinks(res?.data?.link);
      }
    } catch (error: any) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (formik.values.userType) {
      fetchMasterData(formik.values.userType);
    }
  }, [formik.values.userType]);

  useEffect(() => {
    if (id) {
      fetchBanner(id);
    }
  }, [id]);

  return (
    <MainContainer>
      <div className="main_title fdx">
        <div>
          <h1>{id ? 'Edit' : 'Add'} Promotional Campaign</h1>
          <p>
            <span onClick={() => navigate('/dashboard')}>Dashboard</span> -{' '}
            <span onClick={() => navigate('/blogs')}>Promotional Campaigns</span> -{' '}
            {id ? 'Edit' : 'Add'} Promotional Campaign
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_orange" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Campaign Image</label>
              <div className="upload_box_bnr">
                <label>
                  {formik.values.image && (
                    <IconButton
                      className="crossIcon"
                      onClick={() => formik.setFieldValue('image', '')}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                  <figure>
                    <img
                      src={
                        formik.values.image ? formik.values.image : '/static/images/upload_icon.svg'
                      }
                      alt="Upload"
                    />

                    <Input
                      sx={{ display: 'none' }}
                      id="icon-button-file"
                      type="file"
                      inputProps={{
                        accept: 'image/png,image/jpeg',
                      }}
                      onChange={(e: any) => {
                        handleCoverUpload(e);
                        e.target.value = null;
                      }}
                    />
                  </figure>
                  <IconButton className="uploadIcon">
                    <label htmlFor="icon-button-file">
                      <Button component="span" className="upload_image_btn">
                        <img src="/static/images/upload_icon.svg" alt="Upload" />
                        <Input
                          sx={{ display: 'none' }}
                          id="icon-button-file"
                          type="file"
                          inputProps={{
                            accept: 'image/png,image/jpeg',
                          }}
                          onChange={(e: any) => {
                            handleCoverUpload(e);
                            e.target.value = null;
                          }}
                        />
                      </Button>
                    </label>
                  </IconButton>
                </label>
                <FormHelperText error>{formik.touched.image && formik.errors.image}</FormHelperText>
              </div>
            </div>
            <div className="control_group w_50" />
            <div className="control_group w_50">
              <label>Title</label>
              <TextField
                className="text_field"
                variant="outlined"
                placeholder="Title"
                fullWidth
                hiddenLabel
                name="title"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.title}
                helperText={formik.touched.title && formik.errors.title}
                // value={fullName}
                // onChange={(e) => setFullName(e.target.value)}
              />
              {/* {error && fullName == "" ? (
                <h6 className="err_msg">Name is required</h6>
              ) : (
                ""
              )} */}
            </div>
            <div className="control_group w_50">
              <label>Select User Type</label>
              <Select
                fullWidth
                id="userType"
                name="userType"
                value={formik.values.userType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.userType && Boolean(formik.errors.userType)}
                displayEmpty
              >
                <MenuItem disabled value="">
                  Select User Type
                </MenuItem>
                <MenuItem value={USER_TYPE.INDIVIDUAL}>Individual</MenuItem>
                <MenuItem value={USER_TYPE.DEALER}>Dealer</MenuItem>
              </Select>
              <FormHelperText error>
                {formik.touched.userType && typeof formik.errors.userType === 'string'
                  ? formik.errors.userType
                  : ''}
              </FormHelperText>
            </div>
            <div className="control_group w_50">
              <label>Select Banner Type</label>
              <Select
                fullWidth
                id="userType"
                name="bannerType"
                value={formik.values.bannerType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.bannerType && Boolean(formik.errors.bannerType)}
                displayEmpty
              >
                <MenuItem disabled value="">
                  Select User Type
                </MenuItem>
                <MenuItem value={BANNER_TYPE.TOP}>Top</MenuItem>
                <MenuItem value={BANNER_TYPE.BOTTOM}>Bottom</MenuItem>
              </Select>
              <FormHelperText error>
                {formik.touched.bannerType && typeof formik.errors.bannerType === 'string'
                  ? formik.errors.bannerType
                  : ''}
              </FormHelperText>
            </div>
            <div className="control_group w_50">
              <label>Select Seller</label>
              <Select
                fullWidth
                labelId="seller"
                id="seller"
                name="sellerId"
                value={masterData?.user?.length ? formik.values.sellerId : ''}
                onChange={handleSellerChange}
                error={formik.touched.sellerId && Boolean(formik.errors.sellerId)}
                displayEmpty
              >
                <MenuItem disabled value="">
                  Select Seller
                </MenuItem>
                {masterData?.user?.length ? (
                  masterData?.user?.map((item) => (
                    <MenuItem value={item?._id} key={item?._id}>
                      {item?.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled value={1}>
                    No user found
                  </MenuItem>
                )}
              </Select>
              <FormHelperText error>
                {formik.touched.sellerId && formik.errors.sellerId}
              </FormHelperText>
            </div>
            {/* <div className="control_group w_50">
              <label>Promotional Link</label>
              <TextField
                className="text_field"
                variant="outlined"
                placeholder="Promotional Link"
                fullWidth
                hiddenLabel
                name="link"
                value={formik.values.link}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={formik.touched.link && formik.errors.link}
              />
            </div> */}
            <div>
              <label>Add Promotional Link or Upload Video</label>
              {promotionalLinks?.length !== 0
                ? promotionalLinks?.map((item: any, i: any) => (
                    <div className="control_group " key={i}>
                      {item.linkType === 1 ? (
                        <div className="control_group w_50 prom_link">
                          <div
                            className=" "
                            style={
                              {
                                // position:'relative',
                              }
                            }
                          >
                            <label>Promotional Video</label>
                            <figure>
                              <video src={item?.link} title="promotional" />
                              <IconButton
                                className="btn btn_secondary delete_btn"
                                onClick={() => {
                                  setPromotionalLinks([
                                    ...promotionalLinks.filter(
                                      (item: any, index: number) => index !== i
                                    ),
                                  ]);
                                }}
                              >
                                <Delete />
                              </IconButton>
                            </figure>
                          </div>
                        </div>
                      ) : (
                        <div className="control_group w_50 prom_link">
                          <div>
                            <label>Promotional Link</label>
                            <Typography>
                              {item?.link.length > 100
                                ? item?.link.slice(0, 100).concat('...')
                                : item?.link}
                            </Typography>
                          </div>
                          <IconButton
                            className="btn btn_secondary "
                            onClick={() => {
                              setPromotionalLinks([
                                ...promotionalLinks.filter(
                                  (item: any, index: number) => index !== i
                                ),
                              ]);
                            }}
                            // type="submit"
                          >
                            <Delete />
                          </IconButton>
                        </div>
                      )}
                    </div>
                  ))
                : null}
              {promotionalLinks?.length < 10 ? (
                <div className="control_group w_50">
                  <ToggleButtonGroup
                    value={linkType}
                    color="secondary"
                    sx={{
                      mb: 1,
                    }}
                    exclusive
                    onChange={(event, newType) => {
                      if (newType !== null) {
                        setLinkType(newType);
                        setLinkData('');
                      }
                    }}
                    aria-label="link type"
                  >
                    <ToggleButton value="Video" aria-label="Video">
                      Video
                    </ToggleButton>
                    <ToggleButton value="Link" aria-label="Link">
                      Link
                    </ToggleButton>
                  </ToggleButtonGroup>
                  {linkType === 'Video' ? (
                    <div className="control_group w_50">
                      <label>Upload Promotional Video</label>
                      <div className="upload_box_bnr">
                        <label>
                          {linkData && (
                            <IconButton className="crossIcon" onClick={() => setLinkData('')}>
                              <CloseIcon />
                            </IconButton>
                          )}
                          <figure>
                            {linkData ? (
                              <video
                                src={linkData ? linkData : '/static/images/upload_icon.svg'}
                                // alt="Upload"
                                controls
                                autoPlay
                                muted
                              />
                            ) : (
                              <img src={'/static/images/upload_icon.svg'} alt="Upload" />
                            )}
                          </figure>
                          <IconButton className="uploadIcon">
                            <label htmlFor="icon-button-file-2">
                              <Button component="span" className="upload_image_btn">
                                <img src="/static/images/upload_icon.svg" alt="Upload" />
                                <Input
                                  sx={{ display: 'none' }}
                                  id="icon-button-file-2"
                                  type="file"
                                  inputProps={{
                                    accept: '.mp4',
                                  }}
                                  onChange={(e: any) => {
                                    handleLinkVideoUpload(e);
                                    e.target.value = null;
                                  }}
                                />
                              </Button>
                            </label>
                          </IconButton>
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="control_group w_50">
                      <label>Add Promotional Link</label>
                      <TextField
                        className="text_field"
                        variant="outlined"
                        placeholder="Promotional Link"
                        fullWidth
                        hiddenLabel
                        // name="link"
                        value={linkData}
                        onChange={(e) => {
                          if (e.target.value === ' ') return;
                          setLinkData(e.target.value);
                        }}
                        // onBlur={formik.handleBlur}
                        // helperText={formik.touched.link && formik.errors.link}
                      />
                    </div>
                  )}

                  <Button
                    className="btn btn_secondary"
                    onClick={handlePromotional}
                    sx={{
                      mt: 1,
                    }}
                    // onClick={() => navigate("/promotional-campaigns")}
                    // type="submit"
                  >
                    Add
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
          {/* <div className="gap_p "> */}

          {/* </div> */}

          <div className="form_end_btn">
            <Button
              className="btn btn_secondary"
              // onClick={() => navigate("/promotional-campaigns")}
              type="submit"
            >
              Submit
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddPromotionalCampaign;
